import {ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {BicButtonComponent, BicInputComponent, BicPopupComponent} from '@buildinginformationcloud/bic-ui-components';
import {ConnectionSiteView, ExternalConnectionControllerService, Project, ProjectControllerService} from '../../../../api';
import {NotificationsService} from '../../../../services/notifications/notifications.service';

export interface SiteProjectsDialogData {
  organisationId: number;
  connectionId: string;
  site: ConnectionSiteView;
}

/**
 * Projekte zuordnen (Figma Popup 3b): Nur in den ausgewählten Projekten können Nutzer Ordner aus der Site
 * verknüpfen; das Backend prüft das über den SiteAccessGuard. Gespeichert wird der Soll-Stand.
 */
@Component({
  selector: 'app-site-projects-dialog',
  templateUrl: './site-projects-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [BicPopupComponent, BicButtonComponent, BicInputComponent]
})
export class SiteProjectsDialogComponent implements OnInit {
  readonly data = inject<SiteProjectsDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject<MatDialogRef<SiteProjectsDialogComponent>>(MatDialogRef);
  private readonly connectionService = inject(ExternalConnectionControllerService);
  private readonly projectService = inject(ProjectControllerService);
  private readonly notifications = inject(NotificationsService);
  private readonly cdr = inject(ChangeDetectorRef);

  projects: Project[] = [];
  filteredProjects: Project[] = [];
  selected = new Set<number>(this.data.site.projectIds ?? []);
  search = '';
  loading = true;
  busy = false;

  ngOnInit(): void {
    this.projectService.getProjectsByOrganisationId(this.data.organisationId).subscribe({
      next: projects => {
        this.projects = [...projects].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'de'));
        this.filteredProjects = this.projects;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.notifications.showErrorMessage('Die Projekte der Organisation konnten nicht geladen werden.');
        this.cdr.markForCheck();
      }
    });
  }

  onSearch(value: string): void {
    this.search = value ?? '';
    const term = this.search.trim().toLowerCase();
    this.filteredProjects = term ? this.projects.filter(p => (p.name ?? '').toLowerCase().includes(term)) : this.projects;
  }

  toggle(project: Project): void {
    if (this.selected.has(project.id!)) {
      this.selected.delete(project.id!);
    } else {
      this.selected.add(project.id!);
    }
  }

  save(): void {
    this.busy = true;
    this.connectionService.setExternalSiteProjects({projectIds: [...this.selected]}, this.data.organisationId,
      this.data.connectionId, this.data.site.id!).subscribe({
      next: () => {
        this.notifications.showSuccessMessage('Zuordnung gespeichert');
        this.dialogRef.close(true);
      },
      error: err => {
        this.busy = false;
        this.notifications.showErrorMessage(err?.error?.message ?? 'Die Zuordnung konnte nicht gespeichert werden.');
        this.cdr.markForCheck();
      }
    });
  }

  close(): void {
    this.dialogRef.close(false);
  }
}
